// src/layout/builders/ERP/EmployeeTab.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import useERPStore from "../../../store/builders/useERPStore";
import useAuthStore from "../../../store/useAuthStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import toast from "react-hot-toast";
import {
  Plus, Trash2, Edit2, X, Briefcase, Users, Wallet, Phone,
  Calendar, DollarSign, HardHat, MapPin,
  Building2, Star, Layers, RefreshCw, UserCheck,
  CheckSquare, Square, ArrowRight, CheckCircle, CheckCircle2, RotateCcw,
  Clock, AlertCircle, History, Eye, ChevronLeft
} from "lucide-react";

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString("en-IN")}`;

const emptyEmpForm = {
  name: "", position: "", gender: "male", contact: "", daily_salary: "",
  salary_effective_from: new Date().toISOString().split("T")[0],
};

const CONTRACTOR_TYPES = [
  "Tile", "Brick", "Plumbing", "Electrical", "Carpentry", "Painting",
  "Mason", "Flooring", "Roofing", "Civil Work", "Interior Design",
  "Waterproofing", "Steel Work", "Glass Work", "Other",
];

const emptyContractorForm = {
  name: "", company_name: "", contractor_type: "Tile",
  experience: "", contact: "", serviceable_area: "",
};

const TYPE_COLOR = {
  Tile: "bg-amber-50 text-amber-700 border-amber-100",
  Brick: "bg-red-50 text-red-700 border-red-100",
  Plumbing: "bg-blue-50 text-blue-700 border-blue-100",
  Electrical: "bg-yellow-50 text-yellow-700 border-yellow-100",
  Carpentry: "bg-orange-50 text-orange-700 border-orange-100",
  Painting: "bg-pink-50 text-pink-700 border-pink-100",
  Mason: "bg-stone-50 text-stone-700 border-stone-100",
  Flooring: "bg-teal-50 text-teal-700 border-teal-100",
  Roofing: "bg-sky-50 text-sky-700 border-sky-100",
  "Civil Work": "bg-slate-50 text-slate-700 border-slate-100",
  "Interior Design": "bg-purple-50 text-purple-700 border-purple-100",
  Waterproofing: "bg-cyan-50 text-cyan-700 border-cyan-100",
  "Steel Work": "bg-zinc-50 text-zinc-700 border-zinc-100",
  "Glass Work": "bg-indigo-50 text-indigo-700 border-indigo-100",
  Other: "bg-gray-50 text-gray-700 border-gray-100",
};

const STATUS_CFG = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  completed: { label: "Completed", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  revert_requested: { label: "Revert Requested", cls: "bg-amber-100 text-amber-700 border-amber-200" },
};

const EmployeeTab = () => {
  const { user } = useAuthStore();
  const builderId = user?.builderId || user?.builder_id || user?.id;

  const {
    employees, contractors, isLoading,
    fetchEmployees, createEmployee, updateEmployee, deleteEmployee,
    fetchContractors, createContractor, updateContractor, deleteContractor,
    createAssignment, deleteAssignment,
    createContractorAssignment, deleteContractorAssignment,
    approveContractorRevert, rejectContractorRevert,
    fetchContractorRevertRequests,
  } = useERPStore();

  const [activeSection, setActiveSection] = useState("employees");

  // ── Employee form state ──────────────────────────────────────────────────
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [empForm, setEmpForm] = useState(emptyEmpForm);
  const [prevSalary, setPrevSalary] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Contractor form state ────────────────────────────────────────────────
  const [showContractorModal, setShowContractorModal] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [contractorForm, setContractorForm] = useState(emptyContractorForm);
  const [savingContractor, setSavingContractor] = useState(false);

  // ── Employee analytics state ─────────────────────────────────────────────
  const [selectedEmpForDetails, setSelectedEmpForDetails] = useState(null);
  const [empAttendanceFull, setEmpAttendanceFull] = useState({ records: [], advances: [], totalEarned: 0, totalPaid: 0, totalAdvance: 0, balance: 0 });
  const [detailsMonth, setDetailsMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [salaryHistory, setSalaryHistory] = useState([]);
  
  // ── Global Payment & Advance State ──
  const [payingGlobal, setPayingGlobal] = useState(false);
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [showSalaryPayForm, setShowSalaryPayForm] = useState(false);
  const [salaryPayAmount, setSalaryPayAmount] = useState("");
  const [advanceForm, setAdvanceForm] = useState({ amount: "", note: "", advance_date: new Date().toISOString().split("T")[0] });
  const [savingAdvance, setSavingAdvance] = useState(false);
  const advanceFormRef = useRef(null);
  const advanceAmountInputRef = useRef(null);

  // ── Contractor analytics state ───────────────────────────────────────────
  const [selectedContractorForDetails, setSelectedContractorForDetails] = useState(null);
  const [contractorAssignmentsList, setContractorAssignmentsList] = useState([]);
  const [loadingContractorDetails, setLoadingContractorDetails] = useState(false);
  const [contractorPayMode, setContractorPayMode] = useState({}); // { [assignmentId]: 'salary' | 'advance' | null }
  const [contractorPayAmount, setContractorPayAmount] = useState({}); // { [assignmentId]: string }
  const [payingContractorId, setPayingContractorId] = useState(null);

  // ── Projects sub-tab state ───────────────────────────────────────────────
  const [liveProjects, setLiveProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectAssignments, setProjectAssignments] = useState({});
  const [loadingAssignments, setLoadingAssignments] = useState({});
  const [revertRequests, setRevertRequests] = useState([]);
  const [loadingReverts, setLoadingReverts] = useState(false);
  // New: full-page project details view
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetailsFilter, setProjectDetailsFilter] = useState("");
  const [projectDetailsPositionFilter, setProjectDetailsPositionFilter] = useState("all");
  // Edit end date state
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [editEndDate, setEditEndDate] = useState("");
  const [savingEndDate, setSavingEndDate] = useState(false);

  // ── Assign Employees modal ────────────────────────────────────────────────
  const [showEmpAssignModal, setShowEmpAssignModal] = useState(false);
  const [assigningProject, setAssigningProject] = useState(null);
  const [selectedEmpIds, setSelectedEmpIds] = useState(new Set());
  const [empAssignDateForm, setEmpAssignDateForm] = useState({
    start_date: new Date().toISOString().split("T")[0], end_date: "",
  });
  const [submittingEmpAssign, setSubmittingEmpAssign] = useState(false);

  // ── Assign Contractors modal ──────────────────────────────────────────────
  const [showContrAssignModal, setShowContrAssignModal] = useState(false);
  const [selectedContrForAssign, setSelectedContrForAssign] = useState(null);
  const [contrAssignForm, setContrAssignForm] = useState({
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    contract_amount: "",
  });
  const [submittingContrAssign, setSubmittingContrAssign] = useState(false);

  useEffect(() => { fetchEmployees(); fetchContractors(); loadRevertRequests(); }, []); // eslint-disable-line

  useEffect(() => {
    if (activeSection === "projects") {
      if (liveProjects.length === 0) loadProjects();
      else loadRevertRequests();
    }
  }, [activeSection]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const res = await axiosInstancev1.get(`/projects?builderId=${builderId}&status=active&limit=100&page=1`);
      const projects = res.data?.projects || [];
      setLiveProjects(projects);
      loadAllProjectAssignments(projects);
      loadRevertRequests();
    } catch { toast.error("Failed to load projects"); }
    setLoadingProjects(false);
  };

  const loadRevertRequests = async () => {
    setLoadingReverts(true);
    const data = await fetchContractorRevertRequests();
    setRevertRequests(data);
    setLoadingReverts(false);
  };

  const loadAllProjectAssignments = async (projects) => {
    if (!projects.length) return;
    const results = await Promise.allSettled(
      projects.map(p =>
        Promise.all([
          axiosInstancev1.get(`/erp/assignments/project/${p.id}?builderId=${builderId}`),
          axiosInstancev1.get(`/erp/contractor-assignments/project/${p.id}?builderId=${builderId}`),
        ])
      )
    );
    const map = {};
    projects.forEach((p, i) => {
      const result = results[i];
      if (result.status === "fulfilled") {
        const [empRes, contrRes] = result.value;
        const allWorkers = empRes.data?.data || [];
        map[p.id] = {
          employees: allWorkers.filter(w => w.worker_type !== "contractor"),
          contractors: contrRes.data?.data || [],
        };
      } else {
        map[p.id] = { employees: [], contractors: [] };
      }
    });
    setProjectAssignments(map);
  };

  const reloadProjectAssignments = useCallback(async (projectId) => {
    setLoadingAssignments(prev => ({ ...prev, [projectId]: true }));
    try {
      const [empRes, contrRes] = await Promise.all([
        axiosInstancev1.get(`/erp/assignments/project/${projectId}?builderId=${builderId}`),
        axiosInstancev1.get(`/erp/contractor-assignments/project/${projectId}?builderId=${builderId}`),
      ]);
      const allWorkers = empRes.data?.data || [];
      setProjectAssignments(prev => ({
        ...prev,
        [projectId]: {
          employees: allWorkers.filter(w => w.worker_type !== "contractor"),
          contractors: contrRes.data?.data || [],
        },
      }));
    } catch { }
    setLoadingAssignments(prev => ({ ...prev, [projectId]: false }));
  }, [builderId]);

  // ── Employee helpers ─────────────────────────────────────────────────────
  const openEmpModal = (emp = null) => {
    setEditingEmp(emp);
    setPrevSalary(emp ? parseFloat(emp.daily_salary) : null);
    setEmpForm(emp
      ? { name: emp.name, position: emp.position || "", gender: emp.gender || "male", contact: emp.contact || "", daily_salary: emp.daily_salary, salary_effective_from: new Date().toISOString().split("T")[0] }
      : emptyEmpForm);
    setShowEmpModal(true);
  };

  const handleEmpSave = async (e) => {
    e.preventDefault(); setSaving(true);
    const ok = editingEmp ? await updateEmployee(editingEmp.id, empForm) : await createEmployee(empForm);
    setSaving(false);
    if (ok) setShowEmpModal(false);
  };

  const getBadgeStyles = (gender) => {
    if (gender === "female") return "bg-pink-50 text-pink-700 border-pink-100";
    if (gender === "other") return "bg-purple-50 text-purple-700 border-purple-100";
    return "bg-blue-50 text-blue-700 border-blue-100";
  };

  const handleEmpDetailsClick = async (emp) => {
    const employeeId = emp.employee_id || emp.id;
    const employee = {
      id: employeeId,
      name: emp.name || emp.employee_name,
      daily_salary: emp.daily_salary,
      gender: emp.gender,
      position: emp.position
    };
    setSelectedEmpForDetails(employee);
    setActiveSection("employees");
    const tzOffset = new Date().getTimezoneOffset() * 60000;
    const currentMonth = new Date(Date.now() - tzOffset).toISOString().slice(0, 7);
    setDetailsMonth(currentMonth);
    setLoadingDetails(true);
    const [fullData, history] = await Promise.all([
      useERPStore.getState().fetchEmployeeAttendanceFull(employeeId, currentMonth),
      useERPStore.getState().fetchSalaryHistory(employeeId),
    ]);
    setEmpAttendanceFull(fullData);
    setSalaryHistory(history);
    setLoadingDetails(false);
  };

  const handleMonthChange = async (e) => {
    const newMonth = e.target.value;
    setDetailsMonth(newMonth);
    setLoadingDetails(true);
    const fullData = await useERPStore.getState().fetchEmployeeAttendanceFull(selectedEmpForDetails.id, newMonth);
    setEmpAttendanceFull(fullData);
    setLoadingDetails(false);
  };

  // Remaining salary due after advances are deducted from earnings
  const empPayableRemaining = Math.max(0, parseFloat(empAttendanceFull.balance) || 0);

  const openSalaryPayForm = () => {
    setShowAdvanceForm(false);
    setShowSalaryPayForm(v => !v);
    setSalaryPayAmount(empPayableRemaining > 0 ? String(empPayableRemaining) : "");
  };

  const openAdvanceForm = () => {
    setShowSalaryPayForm(false);
    setShowAdvanceForm(true);
    setTimeout(() => {
      advanceFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      advanceAmountInputRef.current?.focus();
    }, 100);
  };

  const handleSalaryPayAmountChange = (value) => {
    if (value === "") { setSalaryPayAmount(""); return; }
    const num = parseFloat(value);
    if (isNaN(num)) return;
    if (num > empPayableRemaining) {
      setSalaryPayAmount(String(empPayableRemaining));
      return;
    }
    setSalaryPayAmount(value);
  };

  const handleSalaryPay = async (e) => {
    e.preventDefault();
    const amount = parseFloat(salaryPayAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Enter a valid amount to pay");
      return;
    }
    if (amount > empPayableRemaining) {
      toast.error(`Cannot pay more than available ${fmt(empPayableRemaining)}`);
      return;
    }
    const unpaidRecords = empAttendanceFull.records
      .filter(r => r.payment_status !== "paid" && r.day_earning > 0)
      .slice()
      .sort((a, b) => new Date(a.attendance_date) - new Date(b.attendance_date));
    if (unpaidRecords.length === 0) {
      toast.error("No unpaid attendance to apply salary against");
      return;
    }
    setPayingGlobal(true);
    try {
      let remaining = amount;
      for (const r of unpaidRecords) {
        if (remaining <= 0) break;
        const payForDay = Math.min(parseFloat(r.day_earning) || 0, remaining);
        if (payForDay <= 0) continue;
        await axiosInstancev1.patch(`/erp/attendance/${r.id}/pay`, { paid_amount: payForDay });
        remaining -= payForDay;
      }
      toast.success(`Salary payment of ${fmt(amount)} recorded!`);
      setShowSalaryPayForm(false);
      setSalaryPayAmount("");
      const fullData = await useERPStore.getState().fetchEmployeeAttendanceFull(selectedEmpForDetails.id, detailsMonth);
      setEmpAttendanceFull(fullData);
    } catch (err) {
      toast.error(err.response?.data?.message || "Payment failed");
    }
    setPayingGlobal(false);
  };

  const handleGlobalAdvanceSave = async (e) => {
    e.preventDefault();
    if (!advanceForm.amount || parseFloat(advanceForm.amount) <= 0) return;
    setSavingAdvance(true);
    try {
      await axiosInstancev1.post("/erp/advances", {
        employee_id: selectedEmpForDetails.id,
        builder_id: builderId,
        amount: advanceForm.amount,
        note: advanceForm.note,
        advance_date: advanceForm.advance_date,
      });
      toast.success("Advance allocated successfully!");
      setShowAdvanceForm(false);
      setAdvanceForm({ amount: "", note: "", advance_date: new Date().toISOString().split("T")[0] });
      const fullData = await useERPStore.getState().fetchEmployeeAttendanceFull(selectedEmpForDetails.id, detailsMonth);
      setEmpAttendanceFull(fullData);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to allocate advance");
    }
    setSavingAdvance(false);
  };

  const toggleContractorPayMode = (assignmentId, mode, remainingBalance) => {
    const closing = contractorPayMode[assignmentId] === mode;
    const nextMode = closing ? null : mode;
    setContractorPayMode(prev => ({ ...prev, [assignmentId]: nextMode }));
    if (closing) {
      setContractorPayAmount(prev => ({ ...prev, [assignmentId]: "" }));
    } else if (mode === "salary" && remainingBalance > 0) {
      setContractorPayAmount(prev => ({ ...prev, [assignmentId]: String(remainingBalance) }));
    } else {
      setContractorPayAmount(prev => ({ ...prev, [assignmentId]: "" }));
    }
  };

  const handleContractorPayAmountChange = (assignmentId, value, maxAmount) => {
    if (value === "") {
      setContractorPayAmount(prev => ({ ...prev, [assignmentId]: "" }));
      return;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return;
    if (num > maxAmount) {
      setContractorPayAmount(prev => ({ ...prev, [assignmentId]: String(maxAmount) }));
      return;
    }
    setContractorPayAmount(prev => ({ ...prev, [assignmentId]: value }));
  };

  const handleContractorPay = async (assignmentId, maxAmount) => {
    const amount = parseFloat(contractorPayAmount[assignmentId]);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (amount > maxAmount) {
      toast.error(`Cannot pay more than available ${fmt(maxAmount)}`);
      return;
    }
    setPayingContractorId(assignmentId);
    const ok = await useERPStore.getState().payContractorAmount(assignmentId, amount);
    if (ok) {
      setContractorPayMode(prev => ({ ...prev, [assignmentId]: null }));
      setContractorPayAmount(prev => ({ ...prev, [assignmentId]: "" }));
      const data = await useERPStore.getState().fetchContractorAssignments(selectedContractorForDetails.id);
      setContractorAssignmentsList(data);
    }
    setPayingContractorId(null);
  };

  // ── Contractor helpers ───────────────────────────────────────────────────
  const handleContractorDetailsClick = async (c) => {
    const contractorId = c.contractor_id || c.id;
    const contractor = {
      id: contractorId,
      name: c.name || c.contractor_name,
      company_name: c.company_name,
      contact: c.contact,
      contractor_type: c.contractor_type
    };
    setSelectedContractorForDetails(contractor);
    setActiveSection("contractors");
    setLoadingContractorDetails(true);
    const data = await useERPStore.getState().fetchContractorAssignments(contractorId);
    setContractorAssignmentsList(data);
    setLoadingContractorDetails(false);
  };


  const openContractorModal = (c = null) => {
    setEditingContractor(c);
    setContractorForm(c
      ? { name: c.name, company_name: c.company_name || "", contractor_type: c.contractor_type, experience: c.experience || "", contact: c.contact || "", serviceable_area: c.serviceable_area || "" }
      : emptyContractorForm);
    setShowContractorModal(true);
  };

  const handleContractorSave = async (e) => {
    e.preventDefault(); setSavingContractor(true);
    const ok = editingContractor
      ? await updateContractor(editingContractor.id, contractorForm)
      : await createContractor(contractorForm);
    setSavingContractor(false);
    if (ok) setShowContractorModal(false);
  };

  // ── Employee assign modal ─────────────────────────────────────────────────
  const openEmpAssignModal = (project) => {
    setAssigningProject(project);
    setSelectedEmpIds(new Set());
    setEmpAssignDateForm({ start_date: new Date().toISOString().split("T")[0], end_date: "" });
    setShowEmpAssignModal(true);
  };

  const hasOverlap = (employeeId, selStart, selEnd, currentProjectId) => {
    if (!selStart || !selEnd) return false;
    const sSel = new Date(selStart);
    const eSel = new Date(selEnd);
    for (const projectId in projectAssignments) {
      if (Number(projectId) === Number(currentProjectId)) continue;
      const assignedEmps = projectAssignments[projectId]?.employees || [];
      for (const empAssign of assignedEmps) {
        if (Number(empAssign.employee_id) === Number(employeeId) && empAssign.contract_status === 'active') {
          const sAssign = new Date(empAssign.start_date.split('T')[0]);
          const eAssign = empAssign.end_date ? new Date(empAssign.end_date.split('T')[0]) : null;
          const startOverlap = sAssign <= eSel;
          const endOverlap = !eAssign || eAssign >= sSel;
          if (startOverlap && endOverlap) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const toggleEmpSelect = (id) => setSelectedEmpIds(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const handleEmpAssignSave = async () => {
    if (selectedEmpIds.size === 0) { toast.error("Select at least one employee"); return; }
    if (!empAssignDateForm.start_date) { toast.error("Please set a start date"); return; }
    setSubmittingEmpAssign(true);
    const projectId = assigningProject.id;
    const assignData = { project_id: projectId, start_date: empAssignDateForm.start_date, end_date: empAssignDateForm.end_date || null };
    const results = await Promise.allSettled(
      [...selectedEmpIds].map(id => createAssignment({ employee_id: id, ...assignData }))
    );
    const failed = results.filter(r => r.status === "rejected" || r.value === false).length;
    if (failed === 0) toast.success(`Employees assigned to ${assigningProject.project_name}`);
    else toast.error(`${failed} assignment(s) failed`);
    setSubmittingEmpAssign(false);
    setShowEmpAssignModal(false);
    reloadProjectAssignments(projectId);
  };

  // ── Contractor assign modal ───────────────────────────────────────────────
  const openContrAssignModal = (project, contractor = null) => {
    setAssigningProject(project);
    setSelectedContrForAssign(contractor);
    setContrAssignForm({ start_date: new Date().toISOString().split("T")[0], end_date: "", contract_amount: "" });
    setShowContrAssignModal(true);
  };

  const handleContrAssignSave = async () => {
    if (!selectedContrForAssign) { toast.error("Select a contractor"); return; }
    if (!contrAssignForm.start_date) { toast.error("Please set a start date"); return; }
    if (!contrAssignForm.contract_amount) { toast.error("Please enter the contract amount"); return; }
    setSubmittingContrAssign(true);
    const ok = await createContractorAssignment({
      contractor_id: selectedContrForAssign.id,
      project_id: assigningProject.id,
      start_date: contrAssignForm.start_date,
      end_date: contrAssignForm.end_date || null,
      contract_amount: parseFloat(contrAssignForm.contract_amount),
    });
    if (ok) {
      toast.success(`${selectedContrForAssign.name} assigned to ${assigningProject.project_name}`);
      setShowContrAssignModal(false);
      reloadProjectAssignments(assigningProject.id);
    }
    setSubmittingContrAssign(false);
  };

  const handleRemoveEmpAssignment = async (assignmentId, projectId) => {
    const ok = await deleteAssignment(assignmentId);
    if (ok) reloadProjectAssignments(projectId);
  };

  const handleRemoveContrAssignment = async (assignmentId, projectId) => {
    const ok = await deleteContractorAssignment(assignmentId);
    if (ok) reloadProjectAssignments(projectId);
  };

  const handleApproveRevert = async (id, projectId) => {
    const ok = await approveContractorRevert(id);
    if (ok) { loadRevertRequests(); reloadProjectAssignments(projectId); }
  };

  const handleRejectRevert = async (id) => {
    const ok = await rejectContractorRevert(id);
    if (ok) loadRevertRequests();
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-white p-4 rounded-xl border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600 border border-orange-100">
            <Users size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-800">Workforce Registry</h2>
            <p className="text-xs text-gray-500 font-medium">
              {employees.length} Employee{employees.length !== 1 ? "s" : ""} &bull; {contractors.length} Contractor{contractors.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {activeSection === "employees" && (
            <button onClick={() => openEmpModal()} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition shadow-sm">
              <Plus size={16} /> Add Employee
            </button>
          )}
          {activeSection === "contractors" && (
            <button onClick={() => openContractorModal()} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-orange-600 transition shadow-sm">
              <HardHat size={16} /> Add Contractor
            </button>
          )}
          {activeSection === "projects" && (
            <button onClick={loadProjects} className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 transition">
              <RefreshCw size={14} /> Refresh
            </button>
          )}
          {activeSection === "projects" && (
            <button onClick={() => { loadProjects(); }} className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 transition">
              <RefreshCw size={14} /> Refresh
            </button>
          )}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg w-fit">
        {[
        { key: "employees", label: `Employees (${employees.length})`, icon: Users, activeColor: "text-blue-600" },
          { key: "contractors", label: `Contractors (${contractors.length})`, icon: HardHat, activeColor: "text-orange-600" },
      { key: "projects", label: "Project Assignment", icon: Layers, activeColor: "text-emerald-600" },
        ].map(({ key, label, icon: Icon, activeColor }) => (
          <button
            key={key}
            onClick={() => { setActiveSection(key); setSelectedEmpForDetails(null); }}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${activeSection === key ? `bg-white ${activeColor} shadow-sm` : "text-gray-500 hover:text-gray-700"}`}
          >
            <span className="flex items-center gap-1.5">
              <Icon size={13} /> {label}
              {key === "projects" && revertRequests.length > 0 && (
                <span className="ml-0.5 bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none">
                  {revertRequests.length}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* ── EMPLOYEES SECTION ── */}
      {activeSection === "employees" && (
        <>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-100 border-t-blue-600"></div>
              <p className="text-sm font-medium text-slate-500">Retrieving personnel details...</p>
            </div>
          ) : employees.length === 0 ? (
            <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-16 text-center flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center text-slate-200 mb-2"><Users size={40} /></div>
              <p className="text-slate-800 font-bold text-lg">No Employees Registered</p>
              <p className="text-slate-500 text-sm max-w-xs mx-auto font-medium">Add workforce members to start assigning them to projects and tracking attendance.</p>
              <button onClick={() => openEmpModal()} className="mt-2 text-blue-600 font-bold text-sm hover:underline">Register first employee</button>
            </div>
          ) : selectedEmpForDetails ? (
            /* ── Employee Full Analytics ── */
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between p-4 border-b bg-gray-50 flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedEmpForDetails(null)} className="p-1 flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors bg-white border border-gray-200 rounded shadow-sm mr-2 w-7 h-7" title="Back">
                    <Plus size={16} className="rotate-45" />
                  </button>
                  <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs border border-blue-100 flex-shrink-0">
                    {selectedEmpForDetails.name.charAt(0).toUpperCase()}
                  </div>
                  <h3 className="font-bold text-gray-800 text-sm ml-1">{selectedEmpForDetails.name}'s Analytics</h3>
                </div>
                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex items-center text-xs text-gray-500 font-medium px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <span className="text-[10px] uppercase font-bold text-gray-400 mr-2">Current Wage</span>
                    {fmt(selectedEmpForDetails.daily_salary)}/day
                  </div>
                  <div className="relative flex items-center rounded-lg shadow-sm">
                    <div className="absolute left-2.5 text-gray-400 pointer-events-none"><Calendar size={14} /></div>
                    <input type="month" value={detailsMonth} onChange={handleMonthChange} className="pl-8 pr-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:border-blue-500 cursor-pointer min-h-[34px]" />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-50/50 space-y-4">
                {loadingDetails ? (
                  <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-100 border-t-blue-600"></div></div>
                ) : (
                  <>
                    {/* Summary stats */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="bg-white rounded-xl p-3 border border-gray-200 flex flex-col gap-1 shadow-sm">
                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Present</span>
                        <div className="text-xl font-black text-emerald-700">
                          {empAttendanceFull.records.filter(a => a.status === "present").length}
                          <span className="text-[10px] font-semibold text-emerald-600/70 ml-0.5">DAYS</span>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-3 border border-gray-200 flex flex-col gap-1 shadow-sm">
                        <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Half Day</span>
                        <div className="text-xl font-black text-amber-700">
                          {empAttendanceFull.records.filter(a => a.status === "half_day").length}
                          <span className="text-[10px] font-semibold text-amber-600/70 ml-0.5">DAYS</span>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-3 border border-gray-200 flex flex-col gap-1 shadow-sm">
                        <span className="text-[10px] font-bold text-rose-600 uppercase tracking-wider">Absent</span>
                        <div className="text-xl font-black text-rose-700">
                          {empAttendanceFull.records.filter(a => a.status === "absent").length}
                          <span className="text-[10px] font-semibold text-rose-600/70 ml-0.5">DAYS</span>
                        </div>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-3 border border-blue-200 flex flex-col gap-1 shadow-sm relative overflow-hidden">
                        <div className="absolute -right-4 -bottom-4 opacity-10"><DollarSign size={64} /></div>
                        <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wider relative z-10">Total Earned</span>
                        <div className="text-xl font-black text-blue-900 truncate relative z-10">{fmt(empAttendanceFull.totalEarned)}</div>
                      </div>
                    </div>

                    {/* Paid / Balance cards */}
                    <div className={`grid ${empAttendanceFull.totalAdvance > 0 ? "grid-cols-3" : "grid-cols-2"} gap-3`}>
                      <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200 flex items-center gap-3 shadow-sm">
                        <CheckCircle size={24} className="text-emerald-500 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Paid</p>
                          <p className="text-lg font-black text-emerald-800">{fmt(empAttendanceFull.totalPaid)}</p>
                        </div>
                      </div>
                      {empAttendanceFull.totalAdvance > 0 && (
                        <div className="bg-violet-50 rounded-xl p-3 border border-violet-200 flex items-center gap-3 shadow-sm">
                          <Wallet size={24} className="text-violet-500 flex-shrink-0" />
                          <div>
                            <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Advance Given</p>
                            <p className="text-lg font-black text-violet-800">{fmt(empAttendanceFull.totalAdvance)}</p>
                          </div>
                        </div>
                      )}
                      <div className={`rounded-xl p-3 border flex items-center gap-3 shadow-sm ${empAttendanceFull.balance > 0 ? "bg-red-50 border-red-200" : (empAttendanceFull.balance < 0 ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200")}`}>
                        <Wallet size={24} className={empAttendanceFull.balance > 0 ? "text-red-500 flex-shrink-0" : (empAttendanceFull.balance < 0 ? "text-blue-500 flex-shrink-0" : "text-gray-400 flex-shrink-0")} />
                        <div>
                          <p className={`text-[10px] font-bold uppercase tracking-wider ${empAttendanceFull.balance > 0 ? "text-red-600" : (empAttendanceFull.balance < 0 ? "text-blue-600" : "text-gray-500")}`}>{empAttendanceFull.balance < 0 ? "Advance Remaining" : "Balance Due"}</p>
                          <p className={`text-lg font-black ${empAttendanceFull.balance > 0 ? "text-red-800" : (empAttendanceFull.balance < 0 ? "text-blue-800" : "text-gray-600")}`}>{empAttendanceFull.balance !== 0 ? fmt(Math.abs(empAttendanceFull.balance)) : "Cleared"}</p>
                        </div>
                      </div>
                      {empAttendanceFull.totalAdvance > 0 && (empAttendanceFull.totalEarned - empAttendanceFull.totalPaid) < empAttendanceFull.totalAdvance && (
                        <div className="col-span-full bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 text-[10px] text-violet-700 font-semibold">
                          Advance covers earnings — employee needs to earn {fmt(empAttendanceFull.totalAdvance - (empAttendanceFull.totalEarned - empAttendanceFull.totalPaid))} more before payment is due
                        </div>
                      )}
                    </div>

                    {/* Action buttons — Salary Pay + Advance allocate only */}
                    <div className="flex justify-end gap-2 mt-1 flex-wrap">
                      <button
                        onClick={openSalaryPayForm}
                        disabled={empPayableRemaining <= 0 && !showSalaryPayForm}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[11px] border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${showSalaryPayForm ? "bg-emerald-600 text-white border-emerald-600 shadow-inner" : "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 shadow-sm"}`}
                      >
                        <DollarSign size={12} /> Salary Pay
                      </button>
                      <button
                        onClick={openAdvanceForm}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[11px] border transition-all ${showAdvanceForm ? "bg-violet-600 text-white border-violet-600 shadow-inner" : "bg-white text-violet-600 border-violet-300 hover:bg-violet-50 shadow-sm"}`}
                      >
                        <Wallet size={12} /> Advance amount allocate
                      </button>
                    </div>

                    {/* Salary Pay form */}
                    {showSalaryPayForm && (
                      <form onSubmit={handleSalaryPay} className="px-4 py-3 bg-emerald-50/60 border border-emerald-100 rounded-xl space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Pay Remaining Salary</p>
                          <p className="text-[10px] font-semibold text-emerald-600">
                            Available to pay: <span className="font-black">{fmt(empPayableRemaining)}</span>
                          </p>
                        </div>
                        {empAttendanceFull.totalAdvance > 0 && (
                          <p className="text-[10px] text-gray-500 font-medium">
                            Advance of {fmt(empAttendanceFull.totalAdvance)} already counted — pay only the remaining balance (earned − advance).
                          </p>
                        )}
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Amount to pay *</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-bold text-sm">₹</span>
                            <input
                              required
                              type="number"
                              min="0.01"
                              step="any"
                              max={empPayableRemaining}
                              placeholder="0"
                              value={salaryPayAmount}
                              onChange={e => handleSalaryPayAmountChange(e.target.value)}
                              className="w-full border border-emerald-200 rounded-lg pl-7 pr-3 py-2 text-sm outline-none focus:border-emerald-400 bg-white font-bold text-emerald-700"
                            />
                          </div>
                          <p className="text-[9px] text-gray-400 mt-1 font-medium">Cannot exceed {fmt(empPayableRemaining)}</p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => { setShowSalaryPayForm(false); setSalaryPayAmount(""); }} className="flex-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50">Cancel</button>
                          <button type="submit" disabled={payingGlobal || empPayableRemaining <= 0} className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1">
                            {payingGlobal ? "Paying..." : "Confirm Salary Pay"}
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Advance form */}
                    {showAdvanceForm && (
                      <form ref={advanceFormRef} onSubmit={handleGlobalAdvanceSave} className="px-4 py-3 bg-violet-50/60 border border-violet-100 rounded-xl space-y-3">
                        <p className="text-[10px] font-bold text-violet-700 uppercase tracking-wider">Advance Amount Allocate</p>
                        <p className="text-[10px] text-gray-500 font-medium">Advance is deducted from salary — only the remaining balance will be due for Salary Pay.</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Amount *</label>
                            <input
                              ref={advanceAmountInputRef}
                              required
                              type="number"
                              min="1"
                              step="any"
                              placeholder="0"
                              value={advanceForm.amount}
                              onChange={e => setAdvanceForm(f => ({ ...f, amount: e.target.value }))}
                              className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet-400 bg-white font-bold text-violet-700"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Date *</label>
                            <input
                              required
                              type="date"
                              value={advanceForm.advance_date}
                              onChange={e => setAdvanceForm(f => ({ ...f, advance_date: e.target.value }))}
                              className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet-400 bg-white"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Note (optional)</label>
                          <input
                            type="text"
                            placeholder="e.g. Festival advance"
                            value={advanceForm.note}
                            onChange={e => setAdvanceForm(f => ({ ...f, note: e.target.value }))}
                            className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet-400 bg-white"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setShowAdvanceForm(false)} className="flex-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50">Cancel</button>
                          <button type="submit" disabled={savingAdvance} className="flex-1 px-3 py-2 bg-violet-600 text-white rounded-lg text-xs font-bold hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-1">
                            {savingAdvance ? "Saving..." : "Allocate Advance"}
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Advances history */}
                    {empAttendanceFull.advances && empAttendanceFull.advances.length > 0 && (
                      <div className="bg-violet-50/30 border border-violet-100 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-4 py-2.5 border-b border-violet-100 flex items-center gap-2">
                          <Wallet size={13} className="text-violet-500" />
                          <span className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Advance History</span>
                        </div>
                        <div className="divide-y divide-violet-100/50 max-h-32 overflow-y-auto">
                          {empAttendanceFull.advances.map(a => (
                            <div key={a.id} className="px-4 py-2 flex items-center justify-between text-xs">
                              <div>
                                <span className="text-gray-500">{new Date(a.advance_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                                {a.note && <span className="text-gray-400 ml-1">— {a.note}</span>}
                                {a.project_name && <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded ml-2">{a.project_name}</span>}
                              </div>
                              <span className="font-bold text-violet-700">{fmt(a.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Salary history */}
                    {salaryHistory.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center gap-2">
                          <History size={13} className="text-gray-400" />
                          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Salary History</span>
                        </div>
                        <div className="divide-y divide-gray-50">
                          {salaryHistory.map((h, i) => (
                            <div key={h.id} className="px-4 py-2 flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2">
                                <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-100 rounded font-bold uppercase">
                                  {i === 0 ? "Latest" : `Prev`}
                                </span>
                                <span className="text-gray-500 font-medium">From {new Date(h.effective_from).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                              </div>
                              <span className="font-bold text-gray-800">{fmt(h.daily_salary)}/day</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Attendance log */}
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                      {empAttendanceFull.records.length === 0 ? (
                        <div className="bg-gray-50/50 p-8 text-center text-gray-400 font-medium text-xs">No attendance logged for {detailsMonth}.</div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase text-gray-500 font-bold tracking-wider">
                            <tr>
                              <th className="text-left px-4 py-2.5">Date</th>
                              <th className="text-left px-4 py-2.5">Status</th>
                              <th className="text-left px-4 py-2.5">Project</th>
                              <th className="text-right px-4 py-2.5">Rate</th>
                              <th className="text-right px-4 py-2.5">Earned</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {empAttendanceFull.records.map(log => (
                              <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                                <td className="px-4 py-2.5 font-bold text-gray-700 whitespace-nowrap">
                                  {new Date(log.attendance_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                                </td>
                                <td className="px-4 py-2.5">
                                  {log.status === "present"
                                    ? <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">Present</span>
                                    : log.status === "half_day"
                                      ? <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">Half Day</span>
                                      : <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">Absent</span>}
                                </td>
                                <td className="px-4 py-2.5 text-gray-600 font-medium truncate max-w-[150px]">{log.project_name || <span className="text-gray-400 italic">—</span>}</td>
                                <td className="px-4 py-2.5 text-right text-gray-500 text-[10px]">{fmt(log.effective_salary)}/d</td>
                                <td className="px-4 py-2.5 text-right font-bold text-gray-700">{log.day_earning > 0 ? fmt(log.day_earning) : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* Employees Table */
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                      <th className="text-left px-4 py-3">Employee Name</th>
                      <th className="text-left px-4 py-3">Position</th>
                      <th className="text-left px-4 py-3">Contact</th>
                      <th className="text-right px-4 py-3">Daily Salary</th>
                      <th className="text-center px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {employees.map((emp) => (
                      <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs border border-blue-100 flex-shrink-0">
                              {emp.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="font-bold text-gray-800 flex items-center gap-2">
                              <button
                                onClick={() => handleEmpDetailsClick(emp)}
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left font-bold transition-all focus:outline-none"
                                title="Click to view full details"
                              >
                                {emp.name}
                              </button>
                              <span className={`text-[8px] px-1 py-0.5 rounded uppercase border flex-shrink-0 ${getBadgeStyles(emp.gender)}`}>{emp.gender}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-medium">
                          <div className="flex items-center gap-1.5"><Briefcase size={14} className="text-gray-400" />{emp.position || "Staff"}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-medium">
                          {emp.contact ? (
                            <button
                              onClick={() => handleEmpDetailsClick(emp)}
                              className="flex items-center gap-1.5 text-blue-600 hover:underline focus:outline-none"
                              title="Click to view full details"
                            >
                              <Phone size={14} className="text-gray-400" />
                              {emp.contact}
                            </button>
                          ) : (
                            <span className="text-gray-400 italic">No contact</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="font-bold text-gray-700 flex items-center justify-end gap-1">
                            <Wallet size={14} className="text-gray-400" />{fmt(emp.daily_salary)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => openEmpModal(emp)} className="p-1.5 text-amber-600 bg-amber-50 hover:bg-amber-600 hover:text-white rounded-lg transition-colors" title="Edit">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={() => deleteEmployee(emp.id)} className="p-1.5 text-red-600 bg-red-50 hover:bg-red-600 hover:text-white rounded-lg transition-colors" title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── CONTRACTORS SECTION ── */}
      {activeSection === "contractors" && (
        <>
          {selectedContractorForDetails ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex items-center justify-between bg-white px-5 py-3 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <button onClick={() => setSelectedContractorForDetails(null)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
                    <ChevronLeft size={18} />
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center font-bold text-sm border border-orange-100">
                      {selectedContractorForDetails.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-800 flex items-center gap-2">
                        {selectedContractorForDetails.name}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase border flex-shrink-0 ${TYPE_COLOR[selectedContractorForDetails.contractor_type] || "bg-gray-50 text-gray-700 border-gray-100"}`}>
                          {selectedContractorForDetails.contractor_type}
                        </span>
                      </h2>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        {selectedContractorForDetails.company_name && <span className="flex items-center gap-1"><Building2 size={12} className="text-gray-400" />{selectedContractorForDetails.company_name}</span>}
                        {selectedContractorForDetails.contact && <span className="flex items-center gap-1"><Phone size={12} className="text-gray-400" />{selectedContractorForDetails.contact}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {loadingContractorDetails ? (
                  <div className="col-span-full py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-100 border-t-orange-500"></div></div>
                ) : contractorAssignmentsList.length === 0 ? (
                  <div className="col-span-full bg-white rounded-xl border border-dashed border-slate-300 py-12 text-center flex flex-col items-center">
                    <HardHat size={32} className="text-slate-300 mb-2" />
                    <p className="text-slate-500 font-medium text-sm">No project assignments found</p>
                  </div>
                ) : (
                  contractorAssignmentsList.map((req) => {
                    const statusCfg = STATUS_CFG[req.contract_status] || { label: req.contract_status, cls: "bg-gray-100 text-gray-700" };
                    return (
                      <div key={req.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:border-orange-200 hover:shadow-md transition-all">
                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                          <div className="font-bold text-gray-800 text-sm">{req.project_name || "Project " + req.project_id}</div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${statusCfg.cls}`}>
                            {statusCfg.label}
                          </span>
                        </div>
                        <div className="p-4 space-y-4">
                          <div className="grid grid-cols-2 gap-4 text-xs">
                            <div>
                              <p className="text-gray-500 mb-1">Contract Period</p>
                              <div className="font-medium text-gray-800 flex items-center gap-1.5">
                                <Calendar size={12} className="text-gray-400" />
                                {new Date(req.start_date).toLocaleDateString("en-GB", { day: '2-digit', month: 'short' })}
                                <span className="text-gray-400 mx-0.5">→</span>
                                {req.end_date ? new Date(req.end_date).toLocaleDateString("en-GB", { day: '2-digit', month: 'short' }) : "Ongoing"}
                              </div>
                            </div>
                            {req.completed_date && (
                              <div>
                                <p className="text-gray-500 mb-1">Completed On</p>
                                <div className="font-medium text-emerald-700 flex items-center gap-1.5">
                                  <CheckCircle size={12} className="text-emerald-500" />
                                  {new Date(req.completed_date).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' })}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-500 font-medium">Contract Value</span>
                              <span className="text-sm font-bold text-gray-800">{fmt(req.contract_amount)}</span>
                            </div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-500 font-medium">Total Paid</span>
                              <span className="text-sm font-bold text-emerald-600">{fmt(req.paid_amount || 0)}</span>
                            </div>
                            <div className="h-px bg-gray-200 w-full my-2 border-dashed"></div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 font-medium">Balance Due</span>
                              <span className="text-sm font-bold text-orange-600">{fmt(Math.max(0, parseFloat(req.contract_amount || 0) - parseFloat(req.paid_amount || 0)))}</span>
                            </div>

                            {(() => {
                              const remaining = Math.max(0, parseFloat(req.contract_amount || 0) - parseFloat(req.paid_amount || 0));
                              if (remaining <= 0) {
                                return (
                                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 text-[10px] text-emerald-700 font-semibold text-center">
                                    Fully paid
                                  </div>
                                );
                              }
                              const mode = contractorPayMode[req.id];
                              return (
                                <div className="space-y-2 pt-1">
                                  <div className="flex gap-2 flex-wrap">
                                    <button
                                      type="button"
                                      onClick={() => toggleContractorPayMode(req.id, "salary", remaining)}
                                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[11px] border transition-all ${mode === "salary" ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"}`}
                                    >
                                      <DollarSign size={12} /> Salary Pay
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => toggleContractorPayMode(req.id, "advance", remaining)}
                                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[11px] border transition-all ${mode === "advance" ? "bg-violet-600 text-white border-violet-600" : "bg-white text-violet-600 border-violet-300 hover:bg-violet-50"}`}
                                    >
                                      <Wallet size={12} /> Advance amount allocate
                                    </button>
                                  </div>

                                  {mode && (
                                    <div className={`rounded-lg px-3 py-3 space-y-2 border ${mode === "salary" ? "bg-emerald-50/60 border-emerald-100" : "bg-violet-50/60 border-violet-100"}`}>
                                      <div className="flex items-center justify-between gap-2">
                                        <p className={`text-[10px] font-bold uppercase tracking-wider ${mode === "salary" ? "text-emerald-700" : "text-violet-700"}`}>
                                          {mode === "salary" ? "Pay Remaining Amount" : "Allocate Advance"}
                                        </p>
                                        <p className={`text-[10px] font-semibold ${mode === "salary" ? "text-emerald-600" : "text-violet-600"}`}>
                                          Available: <span className="font-black">{fmt(remaining)}</span>
                                        </p>
                                      </div>
                                      <div className="relative">
                                        <span className={`absolute left-3 top-1/2 -translate-y-1/2 font-bold text-sm ${mode === "salary" ? "text-emerald-600" : "text-violet-600"}`}>₹</span>
                                        <input
                                          type="number"
                                          min="0.01"
                                          step="any"
                                          max={remaining}
                                          placeholder="0"
                                          value={contractorPayAmount[req.id] || ""}
                                          onChange={e => handleContractorPayAmountChange(req.id, e.target.value, remaining)}
                                          className={`w-full border rounded-lg pl-7 pr-3 py-2 text-sm outline-none bg-white font-bold ${mode === "salary" ? "border-emerald-200 focus:border-emerald-400 text-emerald-700" : "border-violet-200 focus:border-violet-400 text-violet-700"}`}
                                        />
                                      </div>
                                      <p className="text-[9px] text-gray-400 font-medium">Cannot exceed {fmt(remaining)}</p>
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() => toggleContractorPayMode(req.id, mode, remaining)}
                                          className="flex-1 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleContractorPay(req.id, remaining)}
                                          disabled={payingContractorId === req.id || !contractorPayAmount[req.id]}
                                          className={`flex-1 px-3 py-1.5 text-white rounded-lg text-xs font-bold disabled:opacity-50 ${mode === "salary" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-violet-600 hover:bg-violet-700"}`}
                                        >
                                          {payingContractorId === req.id ? "Saving..." : mode === "salary" ? "Confirm Pay" : "Allocate Advance"}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-100 border-t-orange-500"></div>
              <p className="text-sm font-medium text-slate-500">Retrieving contractor details...</p>
            </div>
          ) : contractors.length === 0 ? (
            <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-16 text-center flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center text-slate-200 mb-2"><HardHat size={40} /></div>
              <p className="text-slate-800 font-bold text-lg">No Contractors Registered</p>
              <button onClick={() => openContractorModal()} className="mt-2 text-orange-500 font-bold text-sm hover:underline">Register first contractor</button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                      <th className="text-left px-4 py-3">Contractor</th>
                      <th className="text-left px-4 py-3">Type</th>
                      <th className="text-left px-4 py-3">Company</th>
                      <th className="text-left px-4 py-3">Contact</th>
                      <th className="text-left px-4 py-3">Serviceable Area</th>
                      <th className="text-center px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {contractors.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center font-bold text-xs border border-orange-100 flex-shrink-0">
                              {c.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <button
                                onClick={() => handleContractorDetailsClick(c)}
                                className="font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left transition-all focus:outline-none"
                                title="Click to view full details"
                              >
                                {c.name}
                              </button>
                              {c.experience && (
                                <div className="flex items-center gap-1 text-[10px] text-gray-400 font-medium">
                                  <Star size={10} className="text-amber-400" />{c.experience} yrs exp
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded border font-bold uppercase ${TYPE_COLOR[c.contractor_type] || "bg-gray-50 text-gray-700 border-gray-100"}`}>
                            {c.contractor_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-medium">
                          {c.company_name ? <div className="flex items-center gap-1.5"><Building2 size={13} className="text-gray-400 flex-shrink-0" /><span className="truncate max-w-[120px]">{c.company_name}</span></div> : <span className="text-gray-400 italic">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-medium">
                          {c.contact ? (
                            <button
                              onClick={() => handleContractorDetailsClick(c)}
                              className="flex items-center gap-1.5 text-blue-600 hover:underline focus:outline-none"
                              title="Click to view full details"
                            >
                              <Phone size={13} className="text-gray-400" />
                              {c.contact}
                            </button>
                          ) : (
                            <span className="text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-medium">
                          {c.serviceable_area ? <div className="flex items-center gap-1.5"><MapPin size={13} className="text-gray-400 flex-shrink-0" /><span className="truncate max-w-[140px]">{c.serviceable_area}</span></div> : <span className="text-gray-400 italic">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => openContractorModal(c)} className="p-1.5 text-amber-600 bg-amber-50 hover:bg-amber-600 hover:text-white rounded-lg transition-colors" title="Edit">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={() => deleteContractor(c.id)} className="p-1.5 text-red-600 bg-red-50 hover:bg-red-600 hover:text-white rounded-lg transition-colors" title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── PROJECTS ASSIGNMENT SECTION ── */}
      {activeSection === "projects" && (
        <>
          {loadingProjects ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-100 border-t-emerald-500"></div>
              <p className="text-sm font-medium text-slate-500">Loading live projects...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Revert Requests Banner */}
              {!loadingReverts && revertRequests.length > 0 && (
                <div className="bg-amber-50/80 border border-amber-200 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-100">
                    <RotateCcw size={15} className="text-amber-600 flex-shrink-0" />
                    <span className="font-bold text-amber-700 text-sm">Revert Requests ({revertRequests.length})</span>
                    <span className="text-xs text-amber-600 font-medium ml-1">Contractors requesting to resume work</span>
                  </div>
                  {revertRequests.map(req => (
                    <div key={req.id} className="flex items-center justify-between px-4 py-3 border-b border-amber-100/70 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs border border-amber-200 flex-shrink-0">
                          {(req.name || req.contractor_name || "C").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 text-sm">{req.name || req.contractor_name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {req.contractor_type && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase ${TYPE_COLOR[req.contractor_type] || "bg-gray-50 text-gray-700 border-gray-100"}`}>
                                {req.contractor_type}
                              </span>
                            )}
                            {req.project_name && (
                              <span className="text-[10px] text-gray-500 font-medium">{req.project_name}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleApproveRevert(req.id, req.project_id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition shadow-sm"
                        >
                          <CheckCircle2 size={13} /> Approve Revert
                        </button>
                        <button
                          onClick={() => handleRejectRevert(req.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-500 rounded-lg text-xs font-bold hover:bg-red-50 transition"
                        >
                          <X size={13} /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {liveProjects.length === 0 ? (
                <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-16 text-center flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center text-slate-200 mb-2"><Layers size={40} /></div>
                  <p className="text-slate-800 font-bold text-lg">No Active Projects</p>
                </div>
              ) : selectedProject ? (
                /* ── Full Project Details View ── */
                (() => {
                  const pa = projectAssignments[selectedProject.id] || { employees: [], contractors: [] };
                  const isRefreshing = loadingAssignments[selectedProject.id];

                  // Deduplicate by employee_id, prioritizing active/ongoing and latest start date
                  const empMap = new Map();
                  pa.employees.forEach(emp => {
                    const existing = empMap.get(emp.employee_id);
                    if (!existing) {
                      empMap.set(emp.employee_id, emp);
                      return;
                    }
                    const existingIsOngoing = !existing.end_date;
                    const currentIsOngoing = !emp.end_date;
                    if (currentIsOngoing && !existingIsOngoing) {
                      empMap.set(emp.employee_id, emp);
                    } else if (existingIsOngoing && !currentIsOngoing) {
                      // keep existing
                    } else {
                      const existingStart = new Date(existing.start_date || 0);
                      const currentStart = new Date(emp.start_date || 0);
                      if (currentStart > existingStart) {
                        empMap.set(emp.employee_id, emp);
                      }
                    }
                  });
                  const uniqueEmployees = Array.from(empMap.values());
                  const uniqueContractors = Array.from(new Map(pa.contractors.map(c => [c.id, c])).values());

                  // Get all unique positions
                  const positions = ["all", ...Array.from(new Set(uniqueEmployees.map(e => (e.position || "Staff").trim()).filter(Boolean)))];

                  // Filter employees
                  const searchLower = projectDetailsFilter.toLowerCase();
                  const filteredEmployees = uniqueEmployees.filter(e => {
                    const name = (e.name || e.employee_name || "").toLowerCase();
                    const pos = (e.position || "Staff").toLowerCase();
                    const matchSearch = !searchLower || name.includes(searchLower) || pos.includes(searchLower);
                    const matchPos = projectDetailsPositionFilter === "all" || (e.position || "Staff").trim() === projectDetailsPositionFilter;
                    return matchSearch && matchPos;
                  });

                  // Group employees by position
                  const empByPosition = filteredEmployees.reduce((acc, emp) => {
                    const pos = (emp.position || "Staff").trim();
                    if (!acc[pos]) acc[pos] = [];
                    acc[pos].push(emp);
                    return acc;
                  }, {});

                  const handleSaveEndDate = async (assignmentId, empStatus) => {
                    if (!editEndDate) { toast.error("Select a new end date"); return; }
                    setSavingEndDate(true);
                    const ok = await useERPStore.getState().updateAssignment(assignmentId, { end_date: editEndDate, status: empStatus || 'active' });
                    if (ok) {
                      toast.success("End date updated");
                      setEditingAssignmentId(null);
                      setEditEndDate("");
                      reloadProjectAssignments(selectedProject.id);
                    }
                    setSavingEndDate(false);
                  };

                  return (
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b bg-gray-50 flex-wrap gap-3">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => { setSelectedProject(null); setProjectDetailsFilter(""); setProjectDetailsPositionFilter("all"); }}
                              className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500 transition-colors"
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100 font-bold text-sm">
                              {selectedProject.project_name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-800">{selectedProject.project_name}</h3>
                              <p className="text-[11px] text-gray-500 font-medium">{uniqueEmployees.length} Employees · {uniqueContractors.length} Contractors</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => openEmpAssignModal(selectedProject)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-bold hover:bg-blue-100 transition shadow-sm">
                              <Plus size={13} /> Employee
                            </button>
                            <button onClick={() => openContrAssignModal(selectedProject)} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg text-xs font-bold hover:bg-orange-100 transition shadow-sm">
                              <Plus size={13} /> Contractor
                            </button>
                            <button onClick={() => reloadProjectAssignments(selectedProject.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-200 transition shadow-sm">
                              <RefreshCw size={13} className={isRefreshing ? "animate-spin" : ""} />
                            </button>
                          </div>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap gap-3 px-4 py-3 border-b bg-white">
                          <div className="relative flex-1 min-w-[160px] max-w-xs">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                            </div>
                            <input
                              type="text"
                              placeholder="Search by name or position..."
                              value={projectDetailsFilter}
                              onChange={e => setProjectDetailsFilter(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs font-medium outline-none focus:border-blue-400 transition-colors"
                            />
                          </div>
                          <select
                            value={projectDetailsPositionFilter}
                            onChange={e => setProjectDetailsPositionFilter(e.target.value)}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold bg-white outline-none focus:border-blue-400 text-gray-700"
                          >
                            {positions.map(p => (
                              <option key={p} value={p}>{p === "all" ? "All Positions" : p}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {isRefreshing ? (
                        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-300 border-t-emerald-600"></div></div>
                      ) : (
                        <>
                          {/* Employee tables grouped by position */}
                          {filteredEmployees.length === 0 && uniqueContractors.length === 0 ? (
                            <div className="py-8 text-center bg-white rounded-xl border border-dashed border-gray-200">
                              <p className="text-xs text-gray-400 font-medium">No workforce matches your filter.</p>
                            </div>
                          ) : (
                            <>
                              {Object.entries(empByPosition).map(([position, emps]) => (
                                <div key={position} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                  <div className="px-4 py-2.5 border-b bg-blue-50/60 flex items-center gap-2">
                                    <Users size={13} className="text-blue-500" />
                                    <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider">{position}</span>
                                    <span className="ml-1 bg-blue-100 text-blue-600 text-[9px] font-black px-1.5 py-0.5 rounded-full">{emps.length}</span>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                                          <th className="text-left px-4 py-2.5">Employee</th>
                                          <th className="text-left px-4 py-2.5">Start Date</th>
                                          <th className="text-left px-4 py-2.5">End Date</th>
                                          <th className="text-left px-4 py-2.5">Daily Wage</th>
                                          <th className="text-center px-4 py-2.5">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-50">
                                        {emps.map(emp => (
                                          <tr key={emp.id} className="hover:bg-blue-50/30 transition-colors">
                                            <td className="px-4 py-2.5">
                                              <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                                                  {(emp.name || emp.employee_name || "E").charAt(0).toUpperCase()}
                                                </div>
                                                <button onClick={() => handleEmpDetailsClick(emp)} className="font-bold text-blue-600 hover:underline focus:outline-none text-left">
                                                  {emp.name || emp.employee_name}
                                                </button>
                                              </div>
                                            </td>
                                            <td className="px-4 py-2.5 text-gray-600 font-medium">
                                              {emp.start_date ? new Date(emp.start_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                                            </td>
                                            <td className="px-4 py-2.5">
                                              {editingAssignmentId === emp.id ? (
                                                <div className="flex items-center gap-2">
                                                  <input
                                                    type="date"
                                                    value={editEndDate}
                                                    min={emp.start_date ? emp.start_date.split('T')[0] : undefined}
                                                    onChange={e => setEditEndDate(e.target.value)}
                                                    className="border border-blue-300 rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
                                                  />
                                                  <button onClick={() => handleSaveEndDate(emp.id, emp.contract_status)} disabled={savingEndDate} className="text-emerald-600 hover:text-emerald-800 font-bold text-[10px] disabled:opacity-50">
                                                    {savingEndDate ? "..." : "Save"}
                                                  </button>
                                                  <button onClick={() => { setEditingAssignmentId(null); setEditEndDate(""); }} className="text-gray-400 hover:text-gray-600">
                                                    <X size={12} />
                                                  </button>
                                                </div>
                                              ) : (
                                                <div className="flex items-center gap-2">
                                                  <span className="text-gray-600 font-medium">
                                                    {emp.end_date ? new Date(emp.end_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : <span className="text-gray-400 italic">Ongoing</span>}
                                                  </span>
                                                  <button
                                                    onClick={() => { setEditingAssignmentId(emp.id); setEditEndDate(emp.end_date ? emp.end_date.split('T')[0] : ""); }}
                                                    className="text-amber-500 hover:text-amber-700 transition-colors"
                                                    title="Edit end date"
                                                  >
                                                    <Edit2 size={11} />
                                                  </button>
                                                </div>
                                              )}
                                            </td>
                                            <td className="px-4 py-2.5 font-bold text-gray-700">
                                              {emp.daily_salary ? fmt(emp.daily_salary) + "/day" : "—"}
                                            </td>
                                            <td className="px-4 py-2.5">
                                              <div className="flex items-center justify-center gap-1">
                                                <button onClick={() => handleRemoveEmpAssignment(emp.id, selectedProject.id)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Remove">
                                                  <Trash2 size={13} />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}

                              {/* Contractors table */}
                              {uniqueContractors.length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                  <div className="px-4 py-2.5 border-b bg-orange-50/60 flex items-center gap-2">
                                    <HardHat size={13} className="text-orange-500" />
                                    <span className="text-[11px] font-bold text-orange-700 uppercase tracking-wider">Contractors</span>
                                    <span className="ml-1 bg-orange-100 text-orange-600 text-[9px] font-black px-1.5 py-0.5 rounded-full">{uniqueContractors.length}</span>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                                          <th className="text-left px-4 py-2.5">Contractor</th>
                                          <th className="text-left px-4 py-2.5">Type</th>
                                          <th className="text-left px-4 py-2.5">Period</th>
                                          <th className="text-left px-4 py-2.5">Contract Value</th>
                                          <th className="text-left px-4 py-2.5">Status</th>
                                          <th className="text-center px-4 py-2.5">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-50">
                                        {uniqueContractors.map(c => {
                                          const statusCfg = STATUS_CFG[c.contract_status || "active"];
                                          return (
                                            <tr key={c.id} className="hover:bg-orange-50/30 transition-colors">
                                              <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-2">
                                                  <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                                                    {(c.name || "C").charAt(0).toUpperCase()}
                                                  </div>
                                                  <button onClick={() => handleContractorDetailsClick(c)} className="font-bold text-orange-600 hover:underline focus:outline-none">{c.name}</button>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2.5">
                                                <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold uppercase ${TYPE_COLOR[c.contractor_type] || "bg-gray-50 text-gray-700 border-gray-100"}`}>{c.contractor_type}</span>
                                              </td>
                                              <td className="px-4 py-2.5 text-gray-600 font-medium">
                                                {c.start_date ? new Date(c.start_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}
                                                {c.end_date ? " → " + new Date(c.end_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : " → Ongoing"}
                                              </td>
                                              <td className="px-4 py-2.5 font-bold text-gray-700">{c.contract_amount ? fmt(c.contract_amount) : "—"}</td>
                                              <td className="px-4 py-2.5">
                                                <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold ${statusCfg.cls}`}>{statusCfg.label}</span>
                                              </td>
                                              <td className="px-4 py-2.5">
                                                {(!c.contract_status || c.contract_status === "active") && (
                                                  <div className="flex justify-center">
                                                    <button onClick={() => handleRemoveContrAssignment(c.id, selectedProject.id)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Remove">
                                                      <Trash2 size={13} />
                                                    </button>
                                                  </div>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                          <th className="text-left px-4 py-3">Project</th>
                          <th className="text-left px-4 py-3">Status</th>
                          <th className="text-left px-4 py-3">Workforce</th>
                          <th className="text-center px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {liveProjects.map(project => {
                          const pa = projectAssignments[project.id] || { employees: [], contractors: [] };
                          const uniqueEmployees = Array.from(new Map(pa.employees.map(e => [e.employee_id, e])).values());
                          const uniqueContractors = Array.from(new Map(pa.contractors.map(c => [c.id, c])).values());
                          const totalAssigned = uniqueEmployees.length + uniqueContractors.length;

                          return (
                            <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100 font-bold text-xs flex-shrink-0">
                                    {project.project_name?.charAt(0).toUpperCase() || "P"}
                                  </div>
                                  <p className="font-bold text-gray-800">{project.project_name}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-bold uppercase">Active</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-[11px] text-gray-500 font-semibold">
                                  <UserCheck size={12} className="inline mr-1" />{totalAssigned} assigned
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => openEmpAssignModal(project)}
                                    className="flex items-center gap-1.5 px-2 py-1.5 bg-blue-50 text-blue-600 rounded text-xs font-bold hover:bg-blue-100 transition shadow-sm"
                                  >
                                    <Plus size={13} /> Employee
                                  </button>
                                  <button
                                    onClick={() => openContrAssignModal(project)}
                                    className="flex items-center gap-1.5 px-2 py-1.5 bg-orange-50 text-orange-600 rounded text-xs font-bold hover:bg-orange-100 transition shadow-sm"
                                  >
                                    <Plus size={13} /> Contractor
                                  </button>
                                  <button
                                    onClick={() => { setSelectedProject(project); setProjectDetailsFilter(""); setProjectDetailsPositionFilter("all"); reloadProjectAssignments(project.id); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-800 hover:text-white rounded text-xs font-bold transition shadow-sm"
                                  >
                                    <Eye size={13} /> View Details
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── ADD / EDIT EMPLOYEE MODAL ── */}
      {showEmpModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-800">{editingEmp ? "Edit Employee" : "Register Employee"}</h3>
              <button onClick={() => setShowEmpModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleEmpSave} className="p-4 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Full Name *</label>
                <input required value={empForm.name} onChange={e => setEmpForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Position</label>
                  <input value={empForm.position} onChange={e => setEmpForm(p => ({ ...p, position: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Gender</label>
                  <select value={empForm.gender} onChange={e => setEmpForm(p => ({ ...p, gender: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white">
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Contact (10 Digits)</label>
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="10-digit number"
                    value={empForm.contact}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setEmpForm(p => ({ ...p, contact: val }));
                    }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Daily Wages *</label>
                  <input required type="number" min="0" value={empForm.daily_salary} onChange={e => setEmpForm(p => ({ ...p, daily_salary: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 font-bold text-blue-600" />
                </div>
              </div>
              {/* Show effective date field when editing and salary changed */}
              {editingEmp && empForm.daily_salary && parseFloat(empForm.daily_salary) !== prevSalary && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-[11px] text-blue-700 font-bold">
                    <History size={13} /> Salary change detected — specify effective date
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Effective From *</label>
                    <input type="date" required value={empForm.salary_effective_from} onChange={e => setEmpForm(p => ({ ...p, salary_effective_from: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white" />
                  </div>
                  <p className="text-[10px] text-blue-600 font-medium">Attendance before this date will use {fmt(prevSalary)}/day. New rate {fmt(empForm.daily_salary)} applies from this date.</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEmpModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT CONTRACTOR MODAL ── */}
      {showContractorModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b bg-orange-50/50">
              <div className="flex items-center gap-2">
                <HardHat size={18} className="text-orange-500" />
                <h3 className="font-bold text-gray-800">{editingContractor ? "Edit Contractor" : "Register Contractor"}</h3>
              </div>
              <button onClick={() => setShowContractorModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleContractorSave} className="p-4 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Contractor Type *</label>
                <select required value={contractorForm.contractor_type} onChange={e => setContractorForm(p => ({ ...p, contractor_type: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400 bg-white transition-colors">
                  {CONTRACTOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Full Name *</label>
                  <input required value={contractorForm.name} onChange={e => setContractorForm(p => ({ ...p, name: e.target.value }))} placeholder="John Doe" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Company Name</label>
                  <input value={contractorForm.company_name} onChange={e => setContractorForm(p => ({ ...p, company_name: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Experience (Years)</label>
                  <input type="number" min="0" value={contractorForm.experience} onChange={e => setContractorForm(p => ({ ...p, experience: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Contact Number (10 Digits)</label>
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="10-digit number"
                    value={contractorForm.contact}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setContractorForm(p => ({ ...p, contact: val }));
                    }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Serviceable Area</label>
                <input value={contractorForm.serviceable_area} onChange={e => setContractorForm(p => ({ ...p, serviceable_area: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400 transition-colors" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowContractorModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={savingContractor} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-50">
                  {savingContractor ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ASSIGN EMPLOYEES MODAL ── */}
      {showEmpAssignModal && assigningProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg border border-gray-200 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b bg-blue-50/60 flex-shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-blue-600" />
                  <h3 className="font-bold text-gray-800 text-sm">Assign Employees</h3>
                </div>
                <p className="text-[11px] text-blue-700 font-semibold mt-0.5">{assigningProject.project_name}</p>
              </div>
              <button onClick={() => setShowEmpAssignModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Start Date *</label>
                  <input type="date" required value={empAssignDateForm.start_date} onChange={e => {
                    const val = e.target.value;
                    setEmpAssignDateForm(f => ({ ...f, start_date: val }));
                    setSelectedEmpIds(new Set());
                  }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">End Date *</label>
                  <input type="date" required value={empAssignDateForm.end_date} min={empAssignDateForm.start_date} onChange={e => {
                    const val = e.target.value;
                    setEmpAssignDateForm(f => ({ ...f, end_date: val }));
                    setSelectedEmpIds(new Set());
                  }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5"><Users size={12} className="text-blue-500" /> Employees</p>
                  {empAssignDateForm.start_date && empAssignDateForm.end_date && (
                    <span className="text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">{selectedEmpIds.size} selected</span>
                  )}
                </div>
                {!empAssignDateForm.start_date || !empAssignDateForm.end_date ? (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-3 font-semibold text-center">
                    Please select both Start Date and End Date to view available employees.
                  </p>
                ) : (() => {
                  // Employees already actively assigned to this project (prevent duplicate)
                  const alreadyInProject = new Set(
                    (projectAssignments[assigningProject.id]?.employees || [])
                      .filter(e => e.contract_status === 'active')
                      .map(e => e.employee_id)
                  );
                  // Employees with overlap in OTHER projects
                  const available = employees.filter(emp => !hasOverlap(emp.id, empAssignDateForm.start_date, empAssignDateForm.end_date, assigningProject.id));
                  if (available.length === 0 && alreadyInProject.size === 0) {
                    return <p className="text-xs text-gray-400 italic py-2 text-center">No available employees for the selected date range.</p>;
                  }
                  return (
                    <div className="grid grid-cols-1 gap-1 max-h-52 overflow-y-auto pr-1">
                      {employees.map(emp => {
                        const isSelected = selectedEmpIds.has(emp.id);
                        const alreadyAssigned = alreadyInProject.has(emp.id);
                        const hasDateOverlap = hasOverlap(emp.id, empAssignDateForm.start_date, empAssignDateForm.end_date, assigningProject.id);
                        const isDisabled = alreadyAssigned || hasDateOverlap;
                        return (
                          <label key={emp.id} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                            isDisabled ? "opacity-50 cursor-not-allowed bg-gray-50 border-gray-100"
                            : isSelected ? "border-blue-400 bg-blue-50 cursor-pointer"
                            : "border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 cursor-pointer"
                          }`}>
                            <div className={`flex-shrink-0 transition-colors ${isSelected && !isDisabled ? "text-blue-600" : "text-gray-300"}`}>
                              {isSelected && !isDisabled ? <CheckSquare size={16} /> : <Square size={16} />}
                            </div>
                            <input type="checkbox" checked={isSelected} disabled={isDisabled} onChange={() => !isDisabled && toggleEmpSelect(emp.id)} className="sr-only" />
                            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                              {emp.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-800 text-xs truncate">{emp.name}</p>
                              <p className="text-[10px] text-gray-400 truncate">{emp.position || "Staff"} · {fmt(emp.daily_salary)}/day</p>
                            </div>
                            {alreadyAssigned && (
                              <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded flex-shrink-0">In Project</span>
                            )}
                            {!alreadyAssigned && hasDateOverlap && (
                              <span className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded flex-shrink-0">Conflict</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 font-medium">
                ⚠ An employee cannot be assigned to two projects with overlapping dates. They will appear in this project&apos;s attendance tracking.
              </p>
            </div>
            <div className="flex gap-3 p-4 border-t flex-shrink-0 bg-gray-50">
              <button onClick={() => setShowEmpAssignModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-white transition">Cancel</button>
              <button
                onClick={handleEmpAssignSave}
                disabled={submittingEmpAssign || selectedEmpIds.size === 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {submittingEmpAssign ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Assigning...</> : <><ArrowRight size={14} /> Assign {selectedEmpIds.size > 0 ? `${selectedEmpIds.size} Employee${selectedEmpIds.size > 1 ? "s" : ""}` : "Selected"}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ASSIGN CONTRACTOR MODAL ── */}
      {showContrAssignModal && assigningProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg border border-gray-200 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b bg-orange-50/60 flex-shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <HardHat size={16} className="text-orange-500" />
                  <h3 className="font-bold text-gray-800 text-sm">Assign Contractor</h3>
                </div>
                <p className="text-[11px] text-orange-700 font-semibold mt-0.5">{assigningProject.project_name}</p>
              </div>
              <button onClick={() => setShowContrAssignModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-4">
              {/* Date + contract amount */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Start Date *</label>
                  <input type="date" required value={contrAssignForm.start_date} onChange={e => setContrAssignForm(f => ({ ...f, start_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">End Date</label>
                  <input type="date" value={contrAssignForm.end_date} min={contrAssignForm.start_date} onChange={e => setContrAssignForm(f => ({ ...f, end_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-400" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Contract Amount (₹) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                  <input
                    required
                    type="number"
                    min="0"
                    placeholder="e.g. 50000"
                    value={contrAssignForm.contract_amount}
                    onChange={e => setContrAssignForm(f => ({ ...f, contract_amount: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm outline-none focus:border-orange-400 font-bold text-orange-600"
                  />
                </div>
                <p className="text-[10px] text-gray-400 font-medium mt-1">Fixed contract price for this assignment</p>
              </div>

              {/* Contractor selection */}
              <div>
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-2"><HardHat size={12} className="text-orange-500" /> Select Contractor</p>
                {contractors.length === 0
                  ? <p className="text-xs text-gray-400 italic py-2">No contractors registered yet.</p>
                  : (
                    <div className="grid grid-cols-1 gap-1 max-h-52 overflow-y-auto pr-1">
                      {contractors.map(c => {
                        const isSelected = selectedContrForAssign?.id === c.id;
                        return (
                          <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${isSelected ? "border-orange-400 bg-orange-50" : "border-gray-100 hover:border-orange-200 hover:bg-orange-50/30"}`}>
                            <div className={`flex-shrink-0 transition-colors ${isSelected ? "text-orange-500" : "text-gray-300"}`}>
                              {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                            </div>
                            <input type="radio" checked={isSelected} onChange={() => setSelectedContrForAssign(c)} className="sr-only" />
                            <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                              {c.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-800 text-xs truncate">{c.name}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className={`text-[8px] px-1.5 py-0.5 rounded border font-bold uppercase ${TYPE_COLOR[c.contractor_type] || "bg-gray-50 text-gray-700 border-gray-100"}`}>{c.contractor_type}</span>
                                {c.company_name && <span className="text-[10px] text-gray-400 truncate">{c.company_name}</span>}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
              </div>

              <p className="text-[10px] text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 font-medium">
                Contractors can work on multiple projects at the same time. Contract timeline and payments are tracked per project.
              </p>
            </div>
            <div className="flex gap-3 p-4 border-t flex-shrink-0 bg-gray-50">
              <button onClick={() => setShowContrAssignModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-white transition">Cancel</button>
              <button
                onClick={handleContrAssignSave}
                disabled={submittingContrAssign || !selectedContrForAssign || !contrAssignForm.contract_amount}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {submittingContrAssign ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Assigning...</> : <><ArrowRight size={14} /> Assign Contractor</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeTab;
